import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { Box, Container, CircularProgress } from '@mui/material';
import Navigation from './components/Navigation';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { BookmarkProvider } from './contexts/BookmarkContext';
import BreadcrumbNavigation from './components/BreadcrumbNavigation';
import Footer from './components/Footer';
import CacheWrapper from './components/wrappers/CacheWrapper';
import { apiService } from './services/ApiService';
import { HelmetProvider } from 'react-helmet-async';
import MobileBottomNav from './components/MobileBottomNav';
import { CustomThemeProvider } from './contexts/ThemeContext';
import SpiritualParticlesCanvas from './components/common/SpiritualParticlesCanvas';

const Home = lazy(() => import('./components/Home'));
const AllChapters = lazy(() => import('./components/AllChapters'));
const ChapterDetails = lazy(() => import('./components/ChapterDetails'));
const Verses = lazy(() => import('./components/Verses'));
const VerseDetails = lazy(() => import('./components/verse-details/VerseDetails'));
const LifeSituations = lazy(() => import('./components/LifeSituations'));
const BookmarksView = lazy(() => import('./components/BookmarksView'));
const About = lazy(() => import('./components/About'));

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
    <CircularProgress />
  </Box>
);

const AppContent = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [chaptersPage, setChaptersPage] = useState(1);
  const [versesPage, setVersesPage] = useState(1);
  const [cachedChapters, setCachedChapters] = useState({});
  const [cachedVerses, setCachedVerses] = useState({});
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/') {
      setActiveTab(0);
    } else if (location.pathname === '/chapters') {
      setActiveTab(1);
    } else if (location.pathname.match(/\/chapters\/\d+\/verses\/\d+/)) {
      setActiveTab(4);
    } else if (location.pathname.match(/\/chapters\/\d+\/verses$/)) {
      setActiveTab(3);
    } else if (location.pathname.match(/\/chapters\/\d+$/)) {
      setActiveTab(2);
    } else if (location.pathname === '/situations') {
      setActiveTab(5);
    } else if (location.pathname === '/bookmarks') {
      setActiveTab(6);
    } else if (location.pathname === '/about') {
      setActiveTab(7);
    }
  }, [location.pathname]);

  const fetchChapters = useCallback(async ({ signal } = {}) => {
    const itemsPerPage = 5;
    const skip = (chaptersPage - 1) * itemsPerPage;
    const limit = Math.min(itemsPerPage, 18 - skip);

    return apiService.get('/chapters/', {
      params: { skip, limit },
      signal,
    });
  }, [chaptersPage]);

  return (
    <Box sx={{ width: '100%', bgcolor: 'background.default' }}>
      <Navigation 
        activeTab={activeTab} 
        setActiveTab={setActiveTab}
        setChaptersPage={setChaptersPage}
        setVersesPage={setVersesPage}
      />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, pb: { xs: 8, md: 0 } }}>
        <BreadcrumbNavigation />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/chapters" element={
              <CacheWrapper
                cacheKey={`chapters_page_${chaptersPage}`}
                cache={cachedChapters}
                setCache={setCachedChapters}
                fetchData={fetchChapters}
                loading={false}
              >
                {(data) => (
                  <AllChapters 
                    chapters={data || []}
                    showHindi={true}
                    setActiveTab={setActiveTab}
                    page={chaptersPage}
                    setPage={setChaptersPage}
                  />
                )}
              </CacheWrapper>
            } />
            <Route path="/chapters/:chapterNumber" element={
              <ChapterDetails 
                setActiveTab={setActiveTab}
                showHindi={true}
                cachedChapters={cachedChapters}
                setCachedChapters={setCachedChapters}
              />
            } />
            <Route path="/chapters/:chapterNumber/verses" element={
              <Verses 
                showHindi={true}
                page={versesPage}
                setPage={setVersesPage}
                setActiveTab={setActiveTab}
                cachedVerses={cachedVerses}
                setCachedVerses={setCachedVerses}
                cachedChapters={cachedChapters}
                setCachedChapters={setCachedChapters}
              />
            } />
            <Route path="/chapters/:chapterNumber/verses/:verseNumber" element={
              <VerseDetails 
                showHindi={true}
                cachedVerses={cachedVerses}
                setCachedVerses={setCachedVerses}
              />
            } />
            <Route path="/situations" element={<LifeSituations />} />
            <Route path="/bookmarks" element={<BookmarksView />} />
            <Route path="/about" element={<About />} />
          </Routes>
        </Suspense>
      </Container>
    </Box>
  );
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <CustomThemeProvider>
          <LanguageProvider>
            <NavigationProvider>
              <BookmarkProvider>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  minHeight: '100vh',
                  position: 'relative'
                }}>
                  <SpiritualParticlesCanvas />
                  <div className="ambient-orb ambient-orb-1" />
                  <div className="ambient-orb ambient-orb-2" />
                  <AppContent />
                  <Footer />
                  <MobileBottomNav />
                </Box>
              </BookmarkProvider>
            </NavigationProvider>
          </LanguageProvider>
        </CustomThemeProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
